// Special technologies, bought with the run-only wave currency (spoils). They
// give spoils a purpose between waves: most are permanent-for-run modifier
// boosts; Surge is a repeatable timed mega-boost (a deliberate short spike).
// Everything resets when the settlement falls, exactly like spoils.

import type { RunModifiers } from './cards';

export interface TechDef {
  id: string;
  name: string;
  icon: string;
  desc: string;
  baseCost: number; // spoils for the first purchase
  /** Repeatable techs can be bought many times; cost rises each time. */
  repeatable?: boolean;
  costGrowth?: number; // cost multiplier per prior purchase (repeatables)
  /** Permanent-for-run modifier effect (applied per owned level). */
  apply?: (m: RunModifiers, level: number) => void;
  /** Timed production buff fired on each purchase (the Surge mega-boost). */
  buff?: { prodMult: number; duration: number };
}

export const TECHS: TechDef[] = [
  {
    id: 'irrigation',
    name: 'Irrigation',
    icon: '🌊',
    desc: '+30% production.',
    baseCost: 26,
    apply: (m) => (m.productionMult *= 1.3),
  },
  {
    id: 'masonry',
    name: 'Masonry',
    icon: '🧰',
    desc: 'Buildings cost 20% less.',
    baseCost: 20,
    apply: (m) => (m.buildCostMult *= 0.8),
  },
  {
    id: 'cartography',
    name: 'Cartography',
    icon: '🗺️',
    desc: '+30% harvest radius and +25% harvest damage.',
    baseCost: 24,
    apply: (m) => {
      m.harvestRadiusMult *= 1.3;
      m.harvestDamageMult *= 1.25;
    },
  },
  {
    id: 'logistics',
    name: 'Logistics',
    icon: '🚚',
    desc: '+75% auto-collection.',
    baseCost: 20,
    apply: (m) => (m.autoCollectMult *= 1.75),
  },
  {
    id: 'conscription',
    name: 'Conscription',
    icon: '🪖',
    desc: '+45% defense.',
    baseCost: 40, // a real defense investment now: banking many waves, not 3. Cheap
    // permanent defense techs were trivializing the whole threat curve.
    apply: (m) => (m.defenseMult *= 1.45),
  },
  {
    id: 'bulwark',
    name: 'Bulwark',
    icon: '🏰',
    desc: 'Boss prep: +35% defense and +40 flat defense.',
    baseCost: 34, // the pre-gate decision: bankable for the first era boss, but it is
    // a genuine sacrifice (no free economy techs on the side).
    apply: (m) => {
      m.defenseMult *= 1.35;
      m.defenseFlat += 40;
    },
  },
  {
    id: 'surge',
    name: 'Production Surge',
    icon: '⚡',
    desc: '+150% production for 25 seconds. Repeatable.',
    baseCost: 14,
    repeatable: true,
    costGrowth: 1.5,
    buff: { prodMult: 2.5, duration: 25 },
  },
];

export const TECH_BY_ID: Record<string, TechDef> = {};
for (const t of TECHS) TECH_BY_ID[t.id] = t;

/** Spoils cost to buy `tech` given how many times it is already owned. */
export function techCost(tech: TechDef, owned: number): number {
  if (tech.repeatable) return Math.ceil(tech.baseCost * Math.pow(tech.costGrowth ?? 1.4, owned));
  return tech.baseCost;
}
